import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

const MIN_THUMB_SIZE = 24
const SCROLLBAR_INACTIVITY_MS = 800
const HOVER_MEDIA_QUERY = '(hover: hover)'

export const SCROLLBAR_GUTTER_SIZE = 12
export const SCROLLBAR_THUMB_END_INSET = 2

export const getThumbMetrics = ({
  scrollSize,
  clientSize,
  scrollOffset,
  endInset = 0,
}) => {
  if (scrollSize <= clientSize) {
    return null
  }

  const thumbSize = Math.max((clientSize / scrollSize) * clientSize, MIN_THUMB_SIZE)
  const maxThumbOffset = Math.max(clientSize - thumbSize - 2 * endInset, 0)
  const scrollRange = scrollSize - clientSize
  const thumbOffset =
    scrollRange > 0 ? endInset + (scrollOffset / scrollRange) * maxThumbOffset : endInset

  return { size: thumbSize, offset: thumbOffset }
}

export const getReservedGutters = ({
  scrollWidth,
  scrollHeight,
  clientWidth,
  clientHeight,
  gutterSize = SCROLLBAR_GUTTER_SIZE,
  horizontal = false,
}) => {
  let needH = horizontal && scrollWidth > clientWidth
  let needV = scrollHeight > clientHeight

  // Strictly between clientSize-gutter and clientSize: a sibling track would
  // steal just enough to overflow. Equality means the scroller already matches
  // content (inner pack) or is stretched — do not treat that as pending overflow.
  if (
    needH &&
    !needV &&
    scrollHeight > clientHeight - gutterSize &&
    scrollHeight < clientHeight
  ) {
    needV = true
  }
  if (
    needV &&
    horizontal &&
    !needH &&
    scrollWidth > clientWidth - gutterSize &&
    scrollWidth < clientWidth
  ) {
    needH = true
  }

  return { horizontal: needH, vertical: needV }
}

/**
 * Whether the gutter pack should stretch to the pane height.
 * Shrink-wrap only for H-track + no V-track so the H-track sits under the table.
 * Width is always the table+track pack (leftover space stays outside). Overlay
 * panes always fill.
 */
export const getPaneFill = ({
  reserveScrollbarGutter = false,
  hasVerticalGutter = false,
  hasHorizontalGutter = false,
}) => ({
  fillHeight:
    !reserveScrollbarGutter || hasVerticalGutter || !hasHorizontalGutter,
})

/**
 * Reconstruct the pane size from a scroller that may already have tracks, then
 * predict gutters and the client sizes thumbs should use.
 */
export const getGutterThumbLayout = ({
  scrollWidth,
  scrollHeight,
  clientWidth,
  clientHeight,
  committedVertical = false,
  committedHorizontal = false,
  gutterSize = SCROLLBAR_GUTTER_SIZE,
  horizontal = false,
}) => {
  const paneWidth = clientWidth + (committedVertical ? gutterSize : 0)
  const paneHeight = clientHeight + (committedHorizontal ? gutterSize : 0)
  const gutters = getReservedGutters({
    scrollWidth,
    scrollHeight,
    clientWidth: paneWidth,
    clientHeight: paneHeight,
    gutterSize,
    horizontal,
  })

  return {
    gutters,
    thumbClientWidth: paneWidth - (gutters.vertical ? gutterSize : 0),
    thumbClientHeight: paneHeight - (gutters.horizontal ? gutterSize : 0),
  }
}

const toThumb = (metrics, clientSize) =>
  metrics ? { ...metrics, clientSize } : null

const toThumbState = metrics => (metrics ? { size: metrics.size } : null)

const sameThumbSize = (current, next) => {
  if (!current && !next) return true
  if (!current || !next) return false
  return current.size === next.size
}

const applyThumbNodeStyle = (node, axis, metrics) => {
  if (!node || !metrics) return

  if (axis === 'y') {
    node.style.height = `${metrics.size}px`
    node.style.transform = `translateY(${metrics.offset}px)`
    return
  }

  node.style.width = `${metrics.size}px`
  node.style.transform = `translateX(${metrics.offset}px)`
}

const getOverlayThumbs = (scroller, horizontal) => ({
  vertical: toThumb(
    getThumbMetrics({
      scrollSize: scroller.scrollHeight,
      clientSize: scroller.clientHeight,
      scrollOffset: scroller.scrollTop,
    }),
    scroller.clientHeight,
  ),
  horizontal: horizontal
    ? toThumb(
        getThumbMetrics({
          scrollSize: scroller.scrollWidth,
          clientSize: scroller.clientWidth,
          scrollOffset: scroller.scrollLeft,
        }),
        scroller.clientWidth,
      )
    : null,
})

const getGutterThumbs = (
  scroller,
  { hoverCapable, committedGutters, horizontal },
) => {
  if (!hoverCapable) {
    return { vertical: null, horizontal: null }
  }

  const { gutters, thumbClientWidth, thumbClientHeight } = getGutterThumbLayout({
    scrollWidth: scroller.scrollWidth,
    scrollHeight: scroller.scrollHeight,
    clientWidth: scroller.clientWidth,
    clientHeight: scroller.clientHeight,
    committedVertical: committedGutters.vertical,
    committedHorizontal: committedGutters.horizontal,
    horizontal,
  })

  return {
    vertical: gutters.vertical
      ? toThumb(
          getThumbMetrics({
            scrollSize: scroller.scrollHeight,
            clientSize: thumbClientHeight,
            scrollOffset: scroller.scrollTop,
            endInset: SCROLLBAR_THUMB_END_INSET,
          }),
          thumbClientHeight,
        )
      : null,
    horizontal: gutters.horizontal
      ? toThumb(
          getThumbMetrics({
            scrollSize: scroller.scrollWidth,
            clientSize: thumbClientWidth,
            scrollOffset: scroller.scrollLeft,
            endInset: SCROLLBAR_THUMB_END_INSET,
          }),
          thumbClientWidth,
        )
      : null,
  }
}

/**
 * Overlay scrollbar thumbs, inactivity hide, resize observation, and thumb drag.
 * Fade updates are left to the caller via onSync(scroller).
 *
 * Thumb offset is written to the DOM (rAF-coalesced on scroll). React state is
 * only track presence and thumb size.
 *
 * When reserveScrollbarGutter is on (grid only), thumbs stay visible while that axis
 * overflows and tracks are skipped on (hover: none).
 */
const useOverlayScrollbar = ({
  scrollerRef,
  contentRef,
  horizontal = false,
  reserveScrollbarGutter = false,
  onSync,
}) => {
  const hideScrollbarTimeoutRef = useRef(null)
  const dragStateRef = useRef(null)
  const committedGuttersRef = useRef({ vertical: false, horizontal: false })
  const metricsRef = useRef({ vertical: null, horizontal: null })
  const verticalThumbNodeRef = useRef(null)
  const horizontalThumbNodeRef = useRef(null)
  const scrollRafRef = useRef(null)

  const [isActive, setIsActive] = useState(false)
  const [verticalThumb, setVerticalThumb] = useState(null)
  const [horizontalThumb, setHorizontalThumb] = useState(null)
  // Assume hover so SSR/desktop HTML match; touch is corrected after mount.
  const [hoverCapable, setHoverCapable] = useState(true)

  const markActive = useCallback(() => {
    if (reserveScrollbarGutter) return

    setIsActive(true)

    if (hideScrollbarTimeoutRef.current) {
      clearTimeout(hideScrollbarTimeoutRef.current)
    }

    hideScrollbarTimeoutRef.current = setTimeout(() => {
      setIsActive(false)
    }, SCROLLBAR_INACTIVITY_MS)
  }, [reserveScrollbarGutter])

  const applyThumbNodes = useCallback(thumbs => {
    applyThumbNodeStyle(verticalThumbNodeRef.current, 'y', thumbs.vertical)
    applyThumbNodeStyle(horizontalThumbNodeRef.current, 'x', thumbs.horizontal)
  }, [])

  const updateThumbs = useCallback(() => {
    const scroller = scrollerRef.current
    if (!scroller) return

    const thumbs = reserveScrollbarGutter
      ? getGutterThumbs(scroller, {
          hoverCapable,
          committedGutters: committedGuttersRef.current,
          horizontal,
        })
      : getOverlayThumbs(scroller, horizontal)

    metricsRef.current = thumbs
    applyThumbNodes(thumbs)

    setVerticalThumb(current =>
      sameThumbSize(current, thumbs.vertical) ? current : toThumbState(thumbs.vertical),
    )
    setHorizontalThumb(current =>
      sameThumbSize(current, thumbs.horizontal)
        ? current
        : toThumbState(thumbs.horizontal),
    )
    onSync?.(scroller)
  }, [
    scrollerRef,
    horizontal,
    reserveScrollbarGutter,
    hoverCapable,
    onSync,
    applyThumbNodes,
  ])

  useLayoutEffect(() => {
    committedGuttersRef.current = {
      vertical: Boolean(verticalThumb),
      horizontal: Boolean(horizontalThumb),
    }
    applyThumbNodes(metricsRef.current)
  }, [verticalThumb, horizontalThumb, applyThumbNodes])

  useEffect(() => {
    if (!reserveScrollbarGutter) return undefined

    const media = window.matchMedia(HOVER_MEDIA_QUERY)
    const onChange = event => setHoverCapable(event.matches)
    media.addEventListener('change', onChange)
    setHoverCapable(media.matches)

    return () => {
      media.removeEventListener('change', onChange)
    }
  }, [reserveScrollbarGutter])

  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    const content = contentRef.current
    if (!scroller) return undefined

    updateThumbs()

    const resizeObserver = new ResizeObserver(updateThumbs)
    resizeObserver.observe(scroller)

    if (content) {
      resizeObserver.observe(content)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [scrollerRef, contentRef, updateThumbs])

  useEffect(
    () => () => {
      if (hideScrollbarTimeoutRef.current) {
        clearTimeout(hideScrollbarTimeoutRef.current)
      }
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current)
      }
    },
    [],
  )

  const onScroll = useCallback(() => {
    markActive()
    if (scrollRafRef.current != null) return

    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null
      updateThumbs()
    })
  }, [markActive, updateThumbs])

  const beginDrag = useCallback(
    (axis, event) => {
      event.preventDefault()
      event.stopPropagation()

      const scroller = scrollerRef.current
      const thumb =
        axis === 'y' ? metricsRef.current.vertical : metricsRef.current.horizontal
      if (!scroller || !thumb) return

      dragStateRef.current = {
        axis,
        startClient: axis === 'y' ? event.clientY : event.clientX,
        startScroll: axis === 'y' ? scroller.scrollTop : scroller.scrollLeft,
        thumbSize: thumb.size,
        clientSize: thumb.clientSize,
        scrollSize: axis === 'y' ? scroller.scrollHeight : scroller.scrollWidth,
        endInset: reserveScrollbarGutter ? SCROLLBAR_THUMB_END_INSET : 0,
      }

      markActive()
    },
    [scrollerRef, markActive, reserveScrollbarGutter],
  )

  useEffect(() => {
    const handleMouseMove = event => {
      const dragState = dragStateRef.current
      const scroller = scrollerRef.current
      if (!dragState || !scroller) return

      const {
        axis,
        startClient,
        startScroll,
        thumbSize,
        clientSize,
        scrollSize,
        endInset = 0,
      } = dragState

      const delta =
        axis === 'y' ? event.clientY - startClient : event.clientX - startClient
      const maxThumbOffset = clientSize - thumbSize - 2 * endInset
      const scrollRange = scrollSize - clientSize

      if (maxThumbOffset <= 0 || scrollRange <= 0) return

      const scrollDelta = (delta / maxThumbOffset) * scrollRange

      if (axis === 'y') {
        scroller.scrollTop = startScroll + scrollDelta
      } else {
        scroller.scrollLeft = startScroll + scrollDelta
      }
    }

    const handleMouseUp = () => {
      dragStateRef.current = null
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [scrollerRef])

  return {
    isActive,
    markActive,
    verticalThumb,
    horizontalThumb,
    verticalThumbNodeRef,
    horizontalThumbNodeRef,
    onScroll,
    beginDrag,
  }
}

export default useOverlayScrollbar
